import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { maxFlightPassengerCount } from 'constants/flight'
import { getSumExtraGuestsSurcharge, isReservationAvailableForRoom } from 'lib/checkout/cartReservationUtils'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { getCalenderDaysByCheckIn } from 'lib/offer/offerCalendarUtils'
import { scheduleIsCurrent, hasTravellableSchedules, hasSellableSchedule } from 'lib/offer/scheduleStatusUtils'
import { OfferPackageOptionView } from './offerPageSelectors'

/**
 * Creates an offer page package view for the pkg and offer given
 * This view represents the price and availability values for the
 * given package.
 */
export function mapPackageView(
  offer: App.Offer,
  pkg: App.Package,
  rooms: Array<App.Occupants>,
  options: {
    calendar?: App.Calendar;
    airport?: App.Airport;
    luxPlusMember?: boolean;
    checkIn?: moment.Moment;
    rate?: App.OfferAvailableRate;
  } = {},
): OfferPackageOptionView {
  const { airport, calendar, luxPlusMember, checkIn, rate } = options
  const numberOfRooms = rooms.length || 1
  const useDynamicRates = !!offer.property?.useDynamicCancellationPolicies

  let day = calendar?.cheapestDay
  if (checkIn && calendar) {
    day = getCalenderDaysByCheckIn(calendar)[checkIn.format(ISO_DATE_FORMAT)] ?? calendar.cheapestDay
  }

  // our set of values values that are filled in based on the information we are given
  const baseIsAvailable = scheduleIsCurrent(offer.onlinePurchaseSchedule) && !!pkg.price
  let basePrice = 0
  let baseMemberPrice = 0
  let roomPrices: Array<number> = []
  let roomMemberPrices: Array<number> = []
  let baseValue = 0
  let baseMemberValue = 0
  let surcharge = 0
  let taxesAndFees = 0
  let propertyFees = 0
  let flightPrice = 0
  let hotelAvailable = false
  let flightsAvailable = false
  let extraGuestsSurcharge = 0
  let availableRooms = 0

  if (useDynamicRates && rate) {
    basePrice = rate.hotelPrice
    baseMemberPrice = rate.hotelMemberPrice
    roomPrices = rate.roomPrices
    roomMemberPrices = rate.roomMemberPrices
    baseValue = rate.hotelValue
    baseMemberValue = rate.hotelMemberValue
    surcharge = rate.surcharge
    taxesAndFees = rate.taxesAndFees
    propertyFees = rate.propertyFees
    flightPrice = rate.flightPrice
    hotelAvailable = baseIsAvailable && (day?.hotelAvailable ?? true) && hasTravellableSchedules(pkg.schedules, checkIn!.format(ISO_DATE_FORMAT))
    flightsAvailable = hotelAvailable && (day?.flightsAvailable ?? true)
    extraGuestsSurcharge = rate.extraGuestSurcharge
    availableRooms = rate.availableRooms
  } else if (day) {
    basePrice = day.hotelPrice
    baseMemberPrice = day.hotelMemberPrice
    roomPrices = day.roomPrices
    roomMemberPrices = day.roomMemberPrices
    baseValue = day.hotelValue
    baseMemberValue = day.hotelMemberValue
    surcharge = day.surcharge
    taxesAndFees = day.taxesAndFees
    propertyFees = day.propertyFees
    flightPrice = !airport ? 0 : day.flightPrice
    hotelAvailable = baseIsAvailable && (day?.hotelAvailable ?? true) && hasSellableSchedule(pkg.roomRate?.schedules ?? [])
    flightsAvailable = hotelAvailable && (day?.flightsAvailable ?? true)
    extraGuestsSurcharge = getSumExtraGuestsSurcharge(rooms, offer, pkg)
    availableRooms = day.availableRooms
  } else {
    basePrice = pkg.price * numberOfRooms
    baseMemberPrice = pkg.memberPrice * numberOfRooms
    roomPrices = []
    roomMemberPrices = []
    baseValue = pkg.value * numberOfRooms
    baseMemberValue = pkg.memberValue * numberOfRooms
    surcharge = pkg.surcharge ?? 0
    taxesAndFees = pkg.taxesAndFees * numberOfRooms
    propertyFees = pkg.propertyFees * numberOfRooms
    flightPrice = !airport ? 0 : offer.flightPrices[airport.code] ?? 0
    hotelAvailable = baseIsAvailable && hasSellableSchedule(pkg.roomRate?.schedules ?? [])
    flightsAvailable = hotelAvailable
    extraGuestsSurcharge = getSumExtraGuestsSurcharge(rooms, offer, pkg)
  }

  // fields calculated from the base values
  const hotelPrice = basePrice + surcharge + extraGuestsSurcharge
  const hotelTotal = hotelPrice + propertyFees
  const hotelMemberPrice = baseMemberPrice ? baseMemberPrice + surcharge + extraGuestsSurcharge : 0
  const hotelValue = baseValue + surcharge + extraGuestsSurcharge
  const hotelMemberValue = baseMemberValue ? baseMemberValue + surcharge + extraGuestsSurcharge : 0
  const hotelMemberTotal = hotelMemberPrice ? hotelMemberPrice + propertyFees : 0

  const baseHotelPrice = baseMemberPrice > 0 && luxPlusMember ? hotelMemberPrice : hotelPrice
  const baseHotelValue = baseMemberValue > 0 && luxPlusMember ? hotelMemberValue : hotelValue
  const hotelDiscountPercent = Math.floor((baseHotelValue - baseHotelPrice) * 100 / baseHotelValue)

  const baseFlightBundledPrice = baseHotelPrice + flightPrice
  const baseFlightBundledValue = baseHotelValue + flightPrice
  const bundledDiscountPercent = Math.floor((baseFlightBundledValue - baseFlightBundledPrice) * 100 / baseFlightBundledValue)

  const totalPassengers = sumUpOccupancies(rooms)

  return {
    package: pkg,
    hotelPrice,
    hotelMemberPrice,
    hotelTotal,
    hotelMemberTotal,
    basePrice,
    baseMemberPrice,
    roomPrices,
    roomMemberPrices,
    hotelValue,
    hotelMemberValue,
    taxesAndFees,
    propertyFees,
    baseValue,
    surcharge,
    extraGuestsSurcharge,
    flightPrice,
    capacityValid: !rooms || rooms.every(room => isReservationAvailableForRoom(room, pkg.roomRate?.capacities ?? [], offer)),
    hotelAvailable,
    flightsAvailable: flightsAvailable && totalPassengers <= maxFlightPassengerCount,
    hotelDiscountPercent,
    bundledDiscountPercent,
    fromCalendarInfo: !!calendar,
    availableRooms,
    hasAvailableRates: useDynamicRates && !!rate,
  }
}

export default mapPackageView
